import { inject, Injectable } from '@angular/core';
import { TpaSearchCoverageResExtended } from 'src/app/features/tpa/models/http/tpa-http.model';
import {
  CardConfig,
  IconType,
} from 'src/app/shared/components/generic-card/models/card-config.interface';
import { StatusCalculationService } from '../../../../../../../shared/services/status-calculation.service';
import { TpaService } from '../../../../../services/tpa.service';
import { DateService } from '../../../../../../../shared/services/date.service';
import { ActionId } from '../../../../../models/sob.enum';

@Injectable({
  providedIn: 'root',
})
export class TpaGroupViewService {
  private tpaService = inject(TpaService);
  private dateService = inject(DateService);
  private statusCalculationService = inject(StatusCalculationService);

  setCardConfig(item: TpaSearchCoverageResExtended): CardConfig {
    return {
      sections: [
        {
          title: 'Policy information',
          fields: [
            {
              label: 'Policy Number',
              value: item.policyNumber,
            },
            {
              label: 'Legacy Policy Number',
              value: item.legacyPolicyNumber,
            },
            {
              label: 'External Policy Number',
              value: item.externalPolicyNumber,
            },
            {
              label: 'Policy Holder',
              value: item.policyHolder,
            },
            {
              label: 'Insurance Type',
              value: item.insuranceTypeName,
            },
            {
              label: 'Policy Period',
              value: `${this.tpaService.formatDate(item.policyStartDate)} - ${this.tpaService.formatDate(item.policyEndDate)}`,
            },
            {
              label: 'Insurer',
              value: item.insurer,
            },
            {
              label: 'Currency',
              value: item.rateCurrencyIso,
            },
          ],
        },
        {
          title: 'Member Information',
          fields: [
            {
              label: 'Census Line No',
              value: item.censusLineNo,
            },
            {
              label: 'Plan Number',
              value: item.planNumber,
            },
            {
              label: 'Period',
              value: `${this.dateService.formatToDateString(item.dateJoined)} → ${this.dateService.formatToDateString(item.dateLeft)}`,
            },
            {
              label: 'Insured Role',
              value: item.insuredRoleName,
            },
            {
              label: 'Member Id',
              value: item.memberId,
            },
            {
              label: 'Alternative ID',
              value: item.externalId,
            },
            {
              label: 'Telemedicine Activated',
              value: item.telemedicineEnabled ? 'Yes' : 'No',
            },
            {
              label: 'Citizenship',
              value: item.censusCitizenship,
            },
            {
              label: 'Destination / Temporary Residence - Country',
              value: item.censusDestinationCountry,
            },
          ],
        },
        {
          title: 'Contact Information',
          fields: [
            {
              label: 'Assistance Name',
              value: item.assistanceName,
            },
            {
              label: 'Hotline',
              value: item.hotline,
            },
          ],
        },
        {
          title: 'Additional Information',
          fields: [
            {
              label: 'General Underwriting Conditions',
              value: item.underwritingDetails,
            },
            {
              label: 'Individual Underwriting Conditions',
              value: item.individualUnderwritingDescription,
            },
          ],
        },
        {
          title: 'Instructions',
          fields: [
            {
              label: 'Assistance Instructions',
              value: item.assistanceInstructions,
            },
            {
              label: 'Claim Instructions',
              value: item.claimsInstructions,
            },
            {
              label: 'VIP Insured',
              value: item.tpaVipInsured ? 'Yes' : 'No',
            },
            {
              label: 'Local Provider',
              value: item.tpaLocalProvider,
            },
            {
              label: 'Additional Information',
              value: item.tpaAdditionalInformation,
            },
          ],
        },
      ],
      actions: {
        left: [
          {
            id: ActionId.openSobDialog,
            label: item.sobNotExist ? 'Benefits not exist' : 'Benefit Details',
            disabled: item.sobNotExist,
            type: 'primary',
            extraData: {
              planId: item.planId,
              showPdf: !!item.sobUploaded,
            },
          },
          {
            id: ActionId.openPolicyDialog,
            label: !item.policyHasDocument ? 'Policy Details not exist' : 'Policy Details',
            disabled: !item.policyHasDocument,
            type: 'primary',
            extraData: {
              policyId: item.policyId,
              showPdf: true,
            },
          },
        ],
      },
      options: {
        mainClass: 'tpa-group-view-cl',
        statusIcon: {
          type: this.calculateStatusIconType(item),
        },
      },
    };
  }

  calculatePlanYearRange(items: TpaSearchCoverageResExtended[]): {
    startYear: string;
    endYear: string;
  } {
    if (!items || !items.length) {
      return { startYear: '', endYear: '' };
    }

    let minYear: number | null = null;
    let maxYear: number | null = null;

    for (const item of items) {
      const startDateStr = item.dateJoined || item.policyStartDate;
      const parsedStartDate = this.dateService.parseToDate(startDateStr);

      if (parsedStartDate) {
        const year = parsedStartDate.getFullYear();
        minYear = minYear === null ? year : Math.min(minYear, year);
      }

      const endDateStr = item.dateLeft || item.policyEndDate;
      const parsedEndDate = this.dateService.parseToDate(endDateStr);
      if (parsedEndDate) {
        const year = parsedEndDate.getFullYear();
        maxYear = maxYear === null ? year : Math.max(maxYear, year);
      }
    }

    return {
      startYear: minYear !== null ? minYear.toString() : '',
      endYear: maxYear !== null ? maxYear.toString() : '',
    };
  }

  private calculateStatusIconType(item: TpaSearchCoverageResExtended): IconType {
    if (!item) {
      return 'inactive';
    }

    // Use the computed lineStatus to determine the icon color
    return this.statusCalculationService.lineStatusToIconType(item.lineStatus);
  }
}
